import api from './api';

// One stats engine (28 Oct 2026, Docs/one-stats-engine-plan-2026-10-28.md §5.2).
// Every trade statistic on the dashboard comes from GET /api/v1/stats/* with
// the FilterBar's lens; the components only display what comes back.
//
// Dev fixture: with REACT_APP_STATS_MOCK=true on a development server
// (`npm start`), the calls below are answered by a local contract-shaped
// fixture instead of the server, and GET /nav/config reports the engine as on.
// `process.env.NODE_ENV !== 'production'` is a build-time constant, so a
// production build never includes the fixture module.
let devMock = null;
if (process.env.NODE_ENV !== 'production' && process.env.REACT_APP_STATS_MOCK === 'true') {
  // eslint-disable-next-line global-require
  devMock = require('./statsMock.dev').default;
}

// FilterBar date range → engine period (R11). Custom sends from/to instead.
const PERIOD_FOR_RANGE = {
  Last7Days: 'last_7_days',
  Last30Days: 'last_30_days',
  QTD: 'quarter_to_date',
  YTD: 'year_to_date',
  AllTime: 'all'
};

/**
 * The lens (R14) as query params: exactly the FilterBar's fields. Lists are
 * sent repeated (`systems=a&systems=b`), defaults ('all') are not sent.
 */
export function lensParams(filters = {}, reportingCurrency = null) {
  const params = {};
  const range = filters.dateRange || 'YTD';
  if (range === 'Custom') {
    if (filters.customStartDate) params.from = filters.customStartDate;
    if (filters.customEndDate) params.to = filters.customEndDate;
    params.period = 'custom';
  } else {
    params.period = PERIOD_FOR_RANGE[range] || PERIOD_FOR_RANGE.YTD;
  }
  if (Array.isArray(filters.systems) && filters.systems.length > 0) params.systems = filters.systems;
  if (Array.isArray(filters.setups) && filters.setups.length > 0) params.setups = filters.setups;
  if (filters.direction && filters.direction !== 'all') params.direction = filters.direction;
  if (filters.assetType && filters.assetType !== 'all') params.assetType = filters.assetType;
  if (filters.entryType && filters.entryType !== 'all') params.entryType = filters.entryType;
  if (reportingCurrency) params.reportingCurrency = reportingCurrency;
  return params;
}

// `indexes: null` → repeated keys (systems=a&systems=b), the contract's form.
const serializer = { indexes: null };

async function get(path, params) {
  if (devMock) return devMock.get(path, params);
  const response = await api.get(path, { params, paramsSerializer: serializer });
  const body = response.data;
  // Routes answer { success, data } like the rest of the API; accept a bare body too.
  return body && body.data !== undefined && body.success !== undefined ? body.data : body;
}

const statsService = {
  /** All-time totals and every FilterBar dropdown (before R2). */
  getOptions() {
    return get('/stats/options');
  },
  /** Trade P/L card, Total R card, counts (R4/R5 kept apart). */
  getSummary(filters, reportingCurrency) {
    return get('/stats/summary', lensParams(filters, reportingCurrency));
  },
  /** Performance Summary rows. */
  getPeriods(filters, reportingCurrency) {
    return get('/stats/periods', lensParams(filters, reportingCurrency));
  },
  /** Four-Grid panels: groupBy 'system' | 'setup' | 'trade_group'. */
  getGroups(filters, reportingCurrency, groupBy) {
    return get('/stats/groups', { ...lensParams(filters, reportingCurrency), groupBy });
  },
  /** $ heatmap months, best/worst/average month. */
  getMonthly(filters, reportingCurrency) {
    return get('/stats/monthly', lensParams(filters, reportingCurrency));
  },
  /** R distribution buckets. */
  getDistribution(filters, reportingCurrency) {
    return get('/stats/distribution', lensParams(filters, reportingCurrency));
  },
  /**
   * Drill-down: the trades behind a bar. `query` = { bucket } | { group, groupBy } | { month };
   * `entryType` narrows a stacked segment (initial | pyramid). Capped at 2,000 rows
   * (`truncated: true`).
   */
  getTrades(filters, reportingCurrency, query = {}) {
    const lens = lensParams({ ...filters, ...(query.entryType ? { entryType: query.entryType } : {}) }, reportingCurrency);
    const { entryType, ...rest } = query;
    return get('/stats/trades', { ...lens, ...rest });
  }
};

export default statsService;
